// /en/marriage/$nationality — EN per-nationality marriage registration landing
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import type { MarriageDeepEn } from "@/data/marriage-deep-en";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MARRIAGE_NATIONALITIES, getMarriageNationality } from "@/data/marriage-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import ApostilleCrossLinks from "@/components/ApostilleCrossLinks";
import { Heart, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, MapPin, AlertTriangle, Sparkles } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

function enFaqs(countryEn: string, source: string, hague: boolean) {
  return [
    {
      q: `What documents are required to marry a ${countryEn} national in Thailand?`,
      a: `1) Affirmation of Freedom to Marry (single-status affidavit) issued by ${source}; 2) Passport + valid Thai entry stamp / visa; 3) Sworn Thai translation of the affidavit; 4) MFA Department of Consular Affairs legalization of the Thai translation; 5) Thai ID + house registration for the Thai spouse. NYC handles every step under one fixed-price package.`,
    },
    {
      q: `How long does the full marriage process take?`,
      a: `Standard turnaround is 5–10 business days end-to-end (embassy appointment → translation → MFA → district office). Express service in 3 business days is available for an additional THB 3,500.`,
    },
    {
      q: `Which Thai district office (amphur) should we use?`,
      a: `Any amphur in Thailand can register the marriage. NYC recommends Bang Rak, Watthana, or Pathum Wan in Bangkok — staff are experienced with foreign documents, speak English, and offer same-day appointments. Outside Bangkok, Chiang Mai, Phuket, Pattaya, and Koh Samui are also straightforward.`,
    },
    {
      q: `Will our Thai marriage be recognised in ${countryEn}?`,
      a: hague
        ? `Yes. ${countryEn} is a Hague Apostille member, so the Thai marriage certificate (Kor.Ror.2 / Kor.Ror.3) can be translated into ${countryEn === "United States" ? "English" : "your home language"} and apostilled by the Thai MFA. The apostilled document is then directly valid back home — no embassy step required.`
        : `Yes, but the recognition path is consular legalization (not Apostille). NYC will translate the Thai marriage certificate, get it legalised by the Thai MFA, and then attested at the ${countryEn} embassy in Bangkok before you take it home.`,
    },
    {
      q: `Do we need a translator at the amphur on the wedding day?`,
      a: `Most amphurs in Bangkok and tourist provinces have English-speaking officers, but Thai law requires that both parties fully understand what they are signing. NYC provides a court-approved interpreter (English or your native language) at the district office for THB 2,500.`,
    },
    {
      q: `What is the total cost?`,
      a: `Our all-in package for ${countryEn} nationals starts at THB ${MARRIAGE_NATIONALITIES.find((x) => x.countryEn === countryEn)?.serviceFeeTh.toLocaleString() ?? "8,500"} and covers embassy escort, sworn translation, MFA legalization, and amphur booking. Embassy fees (charged directly by ${source}) and any post-marriage apostille for home-country use are additional.`,
    },
  ];
}

export const Route = createFileRoute("/en/marriage/$nationality/")({
  loader: async ({ params }) => {
    const { getMarriageDeepEn } = await import("@/data/marriage-deep-en");
    const deep: MarriageDeepEn | null = getMarriageDeepEn(params.nationality) ?? null;
    const n = getMarriageNationality(params.nationality);
    if (!n) throw notFound();
    return { deep, slug: params.nationality };
  },
  head: ({ params, loaderData }) => {
    const n = getMarriageNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Nationality not found", "/en/marriage/$nationality", "en")}] };
    const url = `${SITE}/en/marriage/${n.slug}`;
    const deep = loaderData?.deep;
    const title = deep
      ? `Marry a ${n.countryEn} citizen in Thailand ${n.flag} — Embassy + Amphur + ${n.countryEn} Spouse Visa | NYC`
      : `Marry a ${n.countryEn} national in Thailand ${n.flag} — from THB ${n.serviceFeeTh.toLocaleString()} | NYC`;
    const desc = deep
      ? `${deep.pathwayIntro.slice(0, 155).trim()}…`
      : `End-to-end marriage registration for ${n.countryEn} citizens in Thailand: embassy affirmation, sworn Thai translation, MFA legalization, amphur booking. From THB ${n.serviceFeeTh.toLocaleString()}. ${n.turnaround}.`;
    const baseFaqs = enFaqs(n.countryEn, n.singleStatusSource, n.apostilleOnly);
    const faqs = deep ? [...baseFaqs, ...deep.extraFaqs] : baseFaqs;
    return {
      meta: [
        { title: ctaTitle(title, "/en/marriage/$nationality", "en") },
        { name: "description", content: ctaDesc(desc, "/en/marriage/$nationality", "en")},
        { name: "keywords", content: `marry ${n.countryEn} Thailand, marriage registration Thailand ${n.countryEn}, affidavit single status ${n.countryEn} embassy Bangkok, foreigner wedding Thailand${deep ? `, ${n.countryEn} spouse visa` : ""}` },
        { property: "og:title", content: ctaTitle(title, "/en/marriage/$nationality", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/marriage/$nationality", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        ...(deep ? [{ property: "og:type", content: "article" } as const] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/marriage/${n.slug}`, `/en/marriage/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Marriage Registration for ${n.countryEn} Nationals in Thailand`,
              serviceType: "International Marriage Registration",
              inLanguage: "en",
              areaServed: [
                { "@type": "Country", name: "Thailand" },
                { "@type": "Country", name: n.countryEn },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: n.serviceFeeTh,
                priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `How to marry a ${n.countryEn} citizen in Thailand`,
              inLanguage: "en",
              totalTime: "P7D",
              step: [
                { "@type": "HowToStep", position: 1, name: "Consult & gather documents", text: `Send copies of the ${n.countryEn} partner's passport and the Thai spouse's ID via LINE @nycli — we confirm requirements within 1 hour.` },
                { "@type": "HowToStep", position: 2, name: "Embassy affirmation", text: `We accompany the ${n.countryEn} partner to ${n.singleStatusSource} to obtain the Affirmation of Freedom to Marry (typically 1–3 days).` },
                { "@type": "HowToStep", position: 3, name: "Sworn Thai translation + MFA", text: "MFA-registered translator translates the affirmation; the Thai MFA Department of Consular Affairs then legalises the translation (1–3 business days)." },
                { "@type": "HowToStep", position: 4, name: "District office (amphur) booking", text: "We pre-book Bang Rak, Watthana, or Pathum Wan amphur, prepare the witnesses, and accompany you on the wedding day. Most appointments are completed within 90 minutes." },
                { "@type": "HowToStep", position: 5, name: "Certificate translation + Apostille", text: `Translate the Thai Kor.Ror.2 / Kor.Ror.3 back into English and ${n.apostilleOnly ? "apostille it for direct recognition in " + n.countryEn : "legalise it through the " + n.countryEn + " embassy in Bangkok"}.` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Marry in Thailand", item: `${SITE}/en/marriage` },
                { "@type": "ListItem", position: 3, name: `${n.countryEn} nationals`, item: url },
              ],
            }),
          },
          ...(deep
            ? [{
                type: "application/ld+json" as const,
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "@id": `${url}#article`,
                  headline: deep.pathwayTitle,
                  description: deep.pathwayIntro,
                  inLanguage: "en",
                  image: `${SITE}/og-default.jpg`,
                  datePublished: "2026-06-17",
                  dateModified: deep.lastReviewed,
                  author: { "@type": "Organization", name: "NYC Language Institute", url: SITE },
                  publisher: {
                    "@type": "Organization",
                    name: "NYC Language Institute",
                    logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
                  },
                  mainEntityOfPage: url,
                }),
              }]
            : []),
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnMarriageNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Nationality not found</h1>
      <Link to="/en/marriage" className="mt-4 inline-block text-amber-300 underline">
        Back to all nationalities
      </Link>
          <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
    </main>
  ),
});

function EnMarriageNationalityPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getMarriageNationality(slug)!;
  const deep = (Route.useLoaderData() as { deep: MarriageDeepEn | null }).deep;
  const baseFaqs = enFaqs(n.countryEn, n.singleStatusSource, n.apostilleOnly);
  const faqs = deep ? [...baseFaqs, ...deep.extraFaqs] : baseFaqs;
  const others = MARRIAGE_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/marriage" className="hover:text-gold">Marry in Thailand</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.countryEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Heart className="h-3.5 w-3.5" /> Marriage Registration · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            Marry a {n.countryEn} national in Thailand
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            End-to-end document handling for {n.countryEn} citizens registering marriage in Thailand — from embassy affirmation through MFA legalization to amphur registration and post-marriage apostille for home-country recognition.
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Package from", v: `THB ${n.serviceFeeTh.toLocaleString()}` },
              { k: "Turnaround", v: n.turnaround },
              { k: "Legalization", v: n.apostilleOnly ? "Apostille" : "Consular" },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD Reg. 0435567000061, established 2010) provides full-service marriage registration for {n.countryEn} citizens in Thailand — embassy escort to {n.singleStatusSource}, sworn Thai translation, MFA legalization, and amphur booking at Bang Rak, Watthana, or Pathum Wan. Packages start at THB {n.serviceFeeTh.toLocaleString()} and complete in {n.turnaround}. 4.9/5 rating from 2,147 verified reviews.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Documents required</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-slate-600">
            Specific to {n.countryEn}: {n.notes}
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">5-step workflow</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-5">
            {[
              { n: 1, t: "Consult", d: "LINE / phone" },
              { n: 2, t: "Affirmation", d: n.singleStatusSource.split(" /")[0] },
              { n: 3, t: "Translate + MFA", d: "Sworn translator" },
              { n: 4, t: "Amphur booking", d: "Bang Rak / Watthana" },
              { n: 5, t: "Certificate + Apostille", d: n.turnaround },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Package pricing</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            Marry a {n.countryEn} citizen from THB {n.serviceFeeTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Embassy escort + Thai translation + MFA + amphur booking</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3-day service available (+THB 3,500)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Court-approved interpreter at amphur (+THB 2,500)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Post-marriage Kor.Ror.2 / Kor.Ror.3 translation + {n.apostilleOnly ? "Apostille" : "embassy legalization"} for use in {n.countryEn}</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {deep && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-700">
              <Sparkles className="h-3.5 w-3.5" /> {n.countryEn} Spouse Visa Pathway · Updated {deep.lastReviewed}
            </div>
            <h2 className="mt-3 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{deep.pathwayTitle}</h2>
            <p className="mt-3 text-[15px] leading-[1.85] text-slate-700">{deep.pathwayIntro}</p>

            {deep.sections.map((sec) => (
              <div key={sec.h2} className="mt-8">
                <h3 className="font-display text-xl font-extrabold text-navy-deep">{sec.h2}</h3>
                <div className="mt-3 space-y-4 text-[15px] leading-[1.85] text-slate-700">
                  {sec.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}

            <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
              <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-navy-deep">
                <MapPin className="h-5 w-5 text-amber-600" /> {deep.embassy.fullName}
              </h3>
              <dl className="mt-3 grid gap-2 text-[14px] text-slate-700 sm:grid-cols-2">
                <div><dt className="font-bold text-navy-deep">Address</dt><dd>{deep.embassy.address}</dd></div>
                <div><dt className="font-bold text-navy-deep">Fee</dt><dd>{deep.embassy.feeNote}</dd></div>
                <div><dt className="font-bold text-navy-deep">Processing</dt><dd>{deep.embassy.processingTime}</dd></div>
                <div><dt className="font-bold text-navy-deep">Appointment</dt><dd><a href={deep.embassy.appointmentUrl} target="_blank" rel="noreferrer" className="text-amber-700 underline hover:text-amber-900 break-all">Official booking portal</a></dd></div>
              </dl>
            </div>

            <div className="mt-8">
              <h3 className="flex items-center gap-2 font-display text-xl font-extrabold text-navy-deep">
                <AlertTriangle className="h-5 w-5 text-rose-600" /> Common pitfalls — and how NYC prevents them
              </h3>
              <ul className="mt-4 space-y-3">
                {deep.pitfalls.map((p) => (
                  <li key={p.title} className="rounded-xl border border-rose-100 bg-rose-50/50 p-4">
                    <div className="font-bold text-navy-deep">{p.title}</div>
                    <p className="mt-1 text-[14px] leading-relaxed text-slate-700">{p.detail}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <h3 className="font-display text-xl font-extrabold text-navy-deep">Recent case studies</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {deep.cases.map((c) => (
                  <article key={c.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Outcome · {c.outcome}</div>
                    <div className="mt-1 font-display font-extrabold text-navy-deep">{c.title}</div>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700">{c.detail}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}



        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Nearby nationalities</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to="/en/marriage/$nationality"
                  params={{ nationality: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      {o.countryEn}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">From THB {o.serviceFeeTh.toLocaleString()} · {o.turnaround}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <ApostilleCrossLinks mode="en" source="nationality" sourceSlug={n.slug} countryLabel={n.countryEn} />
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
    </main>
  );
}
